import { memo } from 'react';

import Text from '@ui/text/text/text';
import Popover from '@ui/popover/popover';
import { EBroadcastTypes, SessionTypesEnum } from '@/types/session.types';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import OptionalComponent from '@ui/optional-component/optional-component';
import { TBroadcastTypePickerOptions } from './broadcast-type-picker.types';

import { useAppSelector } from '@/store/hooks';
import { selectSaveId } from '@/store/slices/project/selectors';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getBroadcastTypeLabel from './get-broadcast-type-label';
import getBroadcastTypeIcon from './get-broadcast-type-icon';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import styles from './broadcast-type-picker.module.css';

type BroadcastTypePickerProps = {
  disabled?: boolean;
  selected?: TBroadcastTypePickerOptions;
  onChange?: (broadcastType: TBroadcastTypePickerOptions) => void;
  allowFireside?: boolean;
  allowBreakouts?: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
  isEditingInfo?: boolean;
};

/*
  Note:
    The "BroadcastTypePicker" is actually a hybrid session type and broadcast type picker.
    If the user selects "On Demand", we update the session type to be "onDemand" and the broadcast type to "rtmps".
    If the user selects any other broadcast type, we update the session type to be "broadcast" and the broadcast type to the selected one.
*/

const BroadcastTypePicker = memo(
  ({
    allowBreakouts,
    allowFireside,
    disabled,
    selected,
    onChange,
    onToggleOpen,
    isEditingInfo,
  }: BroadcastTypePickerProps) => {
    const saveId = useAppSelector(selectSaveId);
    const isUpdatingBroadcastType = saveId === 'broadcast-type-change';

    const { hex } = useGetColorForProjectThemeComponents({
      colorVariable: EColorVariablesV2.accents,
      contrastAgainstAppBackground: true,
    });

    return (
      <>
        <Text color="var(--gray-300)" className="L3 margin-l-8 margin-r-8 margin-b-8">
          Stream with
        </Text>
        <Popover
          closeOnClick
          cycleWithArrowKeys
          backgroundColor="var(--gray-700)"
          contentClassName="margin-t-4"
          radius="12px"
          inheritRadius
          width="auto"
          /* force closed if isEditingInfo is false (not undefined) so that the popover doesn't flash when re-entering "edit state" */
          forceClosed={isEditingInfo === false ? Math.random() : undefined}
          // do not allow editing while we are actively changing types or things get weird if user changes them too fast
          disabled={disabled || isUpdatingBroadcastType}
          renderTriggerButton={({ open }) => {
            return (
              <Button
                backgroundColor={open ? 'var(--gray-800)' : 'var(--gray-900)'}
                hoverBackgroundColor="var(--gray-800)"
                height="84px"
                width="128px"
                disabled={isUpdatingBroadcastType}
                className={`padding-12 height-32 keep-opacity-when-disabled radius-8 ${styles.selectCard}`}
                onClick={() => {
                  onToggleOpen?.(open);
                }}
                hoverChildrenColor={hex}
                data-open={open}
              >
                <Container className={`flex-column ai-flex-start gap-12`} width="132px">
                  <div className="flex gap-10 ai-center">
                    {getBroadcastTypeIcon(selected || EBroadcastTypes.greenroom, 16)}
                    <Text color="var(--gray-200)" className="S3">
                      {getBroadcastTypeLabel(selected || EBroadcastTypes.greenroom)}
                    </Text>
                  </div>
                  <Container className="flex ai-center jc-space-between" width="102px">
                    <Text
                      className={`L2 ${styles.selectCardLabel}`}
                      opacity={disabled || isUpdatingBroadcastType ? 0.3 : 1}
                      hoverColor={disabled || isUpdatingBroadcastType ? undefined : hex}
                    >
                      Select
                    </Text>
                    <Container
                      className="flex ai-center jc-center"
                      style={{
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: '250ms transform',
                      }}
                    >
                      <Icon name={ICONS.SELECT_DROPDOWN_CARET} size={24} color={'var(--gray-700)'} />
                    </Container>
                  </Container>
                </Container>
              </Button>
            );
          }}
        >
          <Button
            backgroundColor="var(--gray-800)"
            hoverBackgroundColor="var(--gray-700)"
            outlineOnFocus={false}
            className="flex ai-center gap-10 radius-0 width-100-percent padding-v-8 padding-h-12 text-left"
            onClick={() => onChange?.(EBroadcastTypes.greenroom)}
          >
            <div className="min-width-16 flex ai-center jc-center" style={{ position: 'relative', left: 0 }}>
              {getBroadcastTypeIcon(EBroadcastTypes.greenroom, 16)}
            </div>
            <Text className="L2" color="var(--gray-100)">
              {getBroadcastTypeLabel(EBroadcastTypes.greenroom)}
            </Text>
          </Button>
          <Button
            backgroundColor="var(--gray-800)"
            hoverBackgroundColor="var(--gray-700)"
            outlineOnFocus={false}
            className="flex ai-center gap-10 radius-0 width-100-percent padding-v-8 padding-h-12 text-left"
            onClick={() => onChange?.(EBroadcastTypes.rtmps)}
          >
            <div className="min-width-16 flex ai-center jc-center">
              {getBroadcastTypeIcon(EBroadcastTypes.rtmps, 14)}
            </div>
            <Text className="L2" color="var(--gray-100)">
              {getBroadcastTypeLabel(EBroadcastTypes.rtmps)}
            </Text>
          </Button>
          <Button
            backgroundColor="var(--gray-800)"
            hoverBackgroundColor="var(--gray-700)"
            outlineOnFocus={false}
            className="flex ai-center radius-0  jc-flex-start gap-10 width-100-percent padding-v-8 padding-h-12 text-left"
            onClick={() => onChange?.(EBroadcastTypes.embed)}
          >
            <div className="min-width-16 flex ai-center jc-center">
              {getBroadcastTypeIcon(EBroadcastTypes.embed, 14)}
            </div>
            <Text className="L2" color="var(--gray-100)">
              {getBroadcastTypeLabel(EBroadcastTypes.embed)}
            </Text>
          </Button>
          <Button
            backgroundColor="var(--gray-800)"
            hoverBackgroundColor="var(--gray-700)"
            outlineOnFocus={false}
            className="flex ai-center radius-0 jc-flex-start gap-10 width-100-percent padding-h-12 text-left"
            onClick={() => onChange?.(SessionTypesEnum.onDemand)}
          >
            <div className="min-width-16 flex ai-center jc-center">
              {getBroadcastTypeIcon(SessionTypesEnum.onDemand, 13)}
            </div>
            <Text className="L2" color="var(--gray-100)">
              On Demand
            </Text>
          </Button>
          <OptionalComponent display={allowFireside}>
            <Button
              backgroundColor="var(--gray-800)"
              hoverBackgroundColor="var(--gray-700)"
              outlineOnFocus={false}
              // extra space allowed on right to prevent 'Meet Fireside' from wrapping
              className="flex ai-center radius-0 jc-flex-start gap-10 width-100-percent padding-v-8 padding-r-4 padding-l-12 text-left"
              onClick={() => onChange?.(EBroadcastTypes.fireside)}
            >
              <div className="min-width-16 flex ai-center jc-center">
                {getBroadcastTypeIcon(EBroadcastTypes.fireside, 12)}
              </div>
              <Text className="L2" color="var(--gray-100)">
                {getBroadcastTypeLabel(EBroadcastTypes.fireside)}
              </Text>
            </Button>
          </OptionalComponent>
          <OptionalComponent display={allowBreakouts}>
            <Button
              backgroundColor="var(--gray-800)"
              hoverBackgroundColor="var(--gray-700)"
              outlineOnFocus={false}
              className="flex ai-center jc-flex-start gap-10 width-100-percent padding-v-8 padding-h-12 text-left"
              onClick={() => onChange?.(SessionTypesEnum.breakoutRooms)}
            >
              <div className="min-width-16">{getBroadcastTypeIcon(SessionTypesEnum.breakoutRooms)}</div>
              <Text className="L2" color="var(--gray-100)">
                {getBroadcastTypeLabel(SessionTypesEnum.breakoutRooms)}
              </Text>
            </Button>
          </OptionalComponent>
        </Popover>
      </>
    );
  }
);

export default BroadcastTypePicker;
